import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { tenantDisplayName } from "./tenantDisplay";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { formatDate } from "../lib/dateTime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import Pager from "../components/ui/Pager";
import { SEARCH_MAX_LENGTH, useDebouncedSearch } from "./useDebouncedSearch";
import { usePagedList } from "./usePagedList";
import TagEditor from "./hq/TagEditor";
import { currenciesOf } from "./charts/currencies";
import { formatMoneyLedger } from "../lib/formatMoney";
import { usePlanNames } from "./hq/usePlanNames";
import { duplicateEmailCustomerId, hqCustomerErrorMessage } from "./hq/hqCustomerErrors";
import { useScreenWrites } from "./screenWrites";
import { PageLoadError } from "./NoAccess";
import { DETACH_STATUSES, tenantStatusLabel } from "./tenantStatus";
import {
  NAME_MAX_LENGTH, NOTES_MAX_LENGTH, PHONE_MAX_LENGTH, COMM_BODY_MAX_LENGTH, COMM_SUBJECT_MAX_LENGTH,
  clampToCodePoints, hasVisibleCharacter, nameProblem, npsProblem,
} from "./hqCustomerRules";
import { Loader2, RefreshCcw, UserPlus, Search, Mail, Phone, MessageSquare, X, ExternalLink, Send } from "lucide-react";

// The lifecycle stages in the backend's order (platform_customers.LIFECYCLE_STAGES). `hintKey` is the
// one-line definition printed under a tile's count.
const STAGES = {
  lead: { labelKey: "hc.stage.lead", hintKey: "hc.stageHint.lead", color: "bg-slate-100 text-slate-800/70" },
  trial: { labelKey: "hc.stage.trial", hintKey: "hc.stageHint.trial", color: "bg-amber-100 text-amber-600" },
  active: { labelKey: "hc.stage.active", hintKey: "hc.stageHint.active", color: "bg-emerald-100 text-emerald-600" },
  at_risk: { labelKey: "hc.stage.at_risk", hintKey: "hc.stageHint.at_risk", color: "bg-rose-400/15 text-rose-600" },
  suspended: { labelKey: "hc.stage.suspended", hintKey: "hc.stageHint.suspended", color: "bg-orange-100 text-orange-700" },
  churned: { labelKey: "hc.stage.churned", hintKey: "hc.stageHint.churned", color: "bg-white shadow-sm text-slate-400" },
};

const PAGE_SIZE = 25;
// The communication channels the server accepts for a logged exchange.
const CHANNELS = ["email", "call", "meeting", "note"];
// Only an http(s) address becomes a link: `website` is data, and a `javascript:` URL must not be clickable.
const isWebUrl = (url) => /^https?:\/\//i.test(url || "");
const NAME_PROBLEM_KEYS = { empty: "hc.nameEmpty", invisible: "hc.nameInvisible", control: "hc.nameControl", tooLong: "hc.nameTooLong" };
const FIELD_LABEL = "block text-xs text-slate-500 mb-1";

export default function HqCustomers() {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  const [stage, setStage] = useState("");
  const [page, setPage] = useState(1);
  const [search, setSearch, q] = useDebouncedSearch(() => setPage(1));
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const { manage: canWrite } = useScreenWrites("hqCustomers");
  const planName = usePlanNames(lang);
  const [syncing, setSyncing] = useState(false);
  // The list on screen is a function of [stage, q, page] and nothing else, so the highlighted
  // stage tile and the rows can never disagree (BUG-113 was a timer that closed over the
  // previous stage). `reload` re-reads the SAME page and filters (after a sync, or after the
  // detail panel or the new-customer form changed a row).
  const { data, totalForCurrent, loading, loadFailed, loadError, reload, retry } = usePagedList({
    path: "/super-admin/hq/customers", filters: { stage, q }, page, setPage, pageSize: PAGE_SIZE,
  }); // data: { total, items, by_stage }

  // Sync is a repair (contract §1.3): every practice is re-derived into its customer row. The
  // summary is { tenants: int, synced: int, failed: [{ tenant_id, code }] }; one failed practice
  // never fails the request, so the failures are named rather than reported as an error.
  const syncFromTenants = async () => {
    setSyncing(true);
    try {
      const { data: summary } = await api.post("/super-admin/hq/customers/sync");
      toast.success(t("hc.syncDone", { synced: summary.synced, tenants: summary.tenants }));
      const failed = Array.isArray(summary.failed) ? summary.failed : [];
      if (failed.length > 0) {
        toast.warning(t("hc.syncFailed", { n: failed.length, slugs: failed.map((f) => f.tenant_id).join(", ") }));
      }
      reload();
    } catch (e) {
      toast.error(hqCustomerErrorMessage(e, t, "hc.syncError"));
    } finally {
      setSyncing(false);
    }
  };

  const pickStage = (key) => { setStage((cur) => (cur === key ? "" : key)); setPage(1); };

  return (
    <div data-testid="hq-customers">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800">{t("hc.title")}</h1>
          <p className="text-slate-500">{t("hc.subtitle")}</p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <Button onClick={syncFromTenants} disabled={syncing} variant="outline" className="border-slate-200 text-slate-800/70" data-testid="sync-tenants">
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />} {t("hc.syncTenants")}
            </Button>
            <Button onClick={() => setCreating(true)} className="bg-sol-gold text-black hover:bg-amber-200" data-testid="new-customer">
              <UserPlus className="w-4 h-4 mr-2" /> {t("hc.new")}
            </Button>
          </div>
        )}
      </div>

      {/* Stage breakdown */}
      {data?.by_stage && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
          {Object.entries(STAGES).map(([key, s]) => (
            <button
              key={key}
              onClick={() => pickStage(key)}
              className={`text-left p-4 rounded-xl border transition ${stage === key ? "border-sol-gold/40 bg-sol-gold/5" : "border-slate-200 bg-white hover:bg-white/[0.04]"}`}
              data-testid={`stage-${key}`}
            >
              <div className={`inline-flex text-xs rounded-full px-2 py-0.5 ${s.color}`}>{t(s.labelKey)}</div>
              <div className="font-sans font-semibold text-3xl mt-2">{data.by_stage[key] || 0}</div>
              <div className="text-xs text-slate-500 mt-1">{t(s.hintKey)}</div>
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mb-4 max-w-md relative">
        <Search className="w-4 h-4 absolute left-3 text-slate-400" />
        <Input placeholder={t("hc.searchPh")} value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-slate-50 border-slate-200" data-testid="hq-search" maxLength={SEARCH_MAX_LENGTH} />
      </div>

      <PageLoadError error={loadFailed && !loading ? loadError : null} onRetry={retry} testId="hq-customers-load-error" />
      {loading && (loadFailed || !data) && <Loader2 className="w-6 h-6 animate-spin text-amber-600" />}

      {data && !loadFailed && (
        <div
          className={`rounded-2xl bg-white border border-slate-200 overflow-hidden transition-opacity ${loading ? "opacity-60" : ""}`}
          aria-busy={loading}
          data-testid="hq-customers-table"
        >
          {/* The rows of the previous filter stay (dimmed) while new filters load; their total does not. */}
          {totalForCurrent !== null && (
            <div className="px-4 py-3 text-xs text-slate-400 border-b border-slate-100" data-testid="hq-customers-total">{t("hc.total", { n: totalForCurrent })}</div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-400 tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">{t("hc.colName")}</th>
                <th className="text-left px-4 py-3">{t("hc.colEmail")}</th>
                <th className="text-left px-4 py-3">{t("hc.colStage")}</th>
                <th className="text-left px-4 py-3">{t("hc.colPlan")}</th>
                <th className="text-left px-4 py-3">{t("hc.colTenant")}</th>
                <th className="text-left px-4 py-3">{t("hc.colNps")}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => (
                <tr key={c.customer_id} onClick={() => setSelected(c.customer_id)}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`customer-${c.customer_id}`}>
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3 text-slate-500">{c.email}</td>
                  <td className="px-4 py-3"><span className={`text-xs rounded-full px-2 py-0.5 ${STAGES[c.lifecycle_stage]?.color || "bg-slate-100"}`}>{STAGES[c.lifecycle_stage] ? t(STAGES[c.lifecycle_stage].labelKey) : c.lifecycle_stage}</span></td>
                  <td className="px-4 py-3 text-xs text-amber-600">{c.tenant_plan ? planName(c.tenant_plan) : "—"}</td>
                  <td className="px-4 py-3"><code className="text-xs text-slate-500">{c.tenant_slug || "—"}</code></td>
                  <td className="px-4 py-3 text-slate-500">{c.nps ?? "—"}</td>
                </tr>
              ))}
              {data.items.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-400" data-testid="hq-customers-empty">{t("hc.empty")}</td></tr>}
            </tbody>
          </table>
          {data.total > PAGE_SIZE && (
            <Pager
              page={page}
              pageSize={PAGE_SIZE}
              total={data.total}
              shown={data.items.length}
              onPage={setPage}
              infoText={(v) => t("sal.pagerInfo", v)}
              prevLabel={t("sal.pagerPrev")}
              nextLabel={t("sal.pagerNext")}
              testId="hq-customers-pager"
              tone="slate"
            />
          )}
        </div>
      )}

      {selected && <CustomerDetail t={t} customerId={selected} canWrite={canWrite} planName={planName} onClose={() => { setSelected(null); reload(); }} />}
      {creating && (
        <NewCustomerModal t={t} onClose={() => setCreating(false)} onDone={() => { setCreating(false); reload(); }}
          onOpenExisting={(id) => { setCreating(false); setSelected(id); }} />
      )}
    </div>
  );
}

// The edit draft keeps only what differs from the stored row. A cleared text field is sent as null
// (the server clears it); one typed back to its stored value is no change at all.
const storedText = (c, key) => c[key] ?? "";
// Stored tags as the editor shows them. A legacy non-text tag is shown (and removable) as text.
const storedTags = (c) => (Array.isArray(c?.tags) ? c.tags.map(String) : []);
const NO_TAG_EDIT = { add: [], remove: [] };

function CustomerDetail({ t, customerId, canWrite, planName, onClose }) {
  const { lang } = useLanguage();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [edit, setEdit] = useState({});
  // Tags are edited as deltas against the stored row (contract §2.2): a save sends what the
  // operator added and removed, never the whole list, so a tag another writer added meanwhile
  // (an automation's `$addToSet`) is kept.
  const [tagEdit, setTagEdit] = useState(NO_TAG_EDIT);
  const [npsText, setNpsText] = useState(null); // null = untouched; otherwise what the input shows
  // A changed score fires `platform_customer.nps_low` (contract 2.4), whose rules run as detached
  // tasks after the save has answered, so what they write (an `nps_followup` tag) lands at a time
  // nobody can know here. The operator is told so, and Refresh shows the row as it is now.
  const [automationsPending, setAutomationsPending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const read = () => api.get(`/super-admin/hq/customers/${customerId}`).then((r) => r.data);
  // The first read (and a retry) starts the draft afresh.
  const load = () => {
    setLoadError(null);
    read().then((d) => { setData(d); setEdit({}); setTagEdit(NO_TAG_EDIT); setNpsText(null); }).catch(setLoadError);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [customerId]);
  // Refresh replaces the stored row only: the draft is a diff against it, so what the operator
  // typed and has not saved survives.
  const refresh = async () => {
    setRefreshing(true);
    try {
      setData(await read());
      setAutomationsPending(false);
    } catch (e) {
      toast.error(hqCustomerErrorMessage(e, t, "hc.refreshFailed"));
    } finally {
      setRefreshing(false);
    }
  };

  const stored = data?.customer;
  const setField = (key, value) => setEdit((cur) => {
    const next = { ...cur };
    if (value === storedText(stored, key)) delete next[key]; else next[key] = value;
    return next;
  });
  const shownTags = () => {
    const kept = storedTags(stored).filter((tag) => !tagEdit.remove.includes(tag));
    return [...kept, ...tagEdit.add.filter((tag) => !kept.includes(tag))];
  };
  const setTags = (next) => {
    const before = storedTags(stored);
    setTagEdit({ add: next.filter((tag) => !before.includes(tag)), remove: before.filter((tag) => !next.includes(tag)) });
  };
  const changeNps = (text) => {
    setNpsText(text);
    if (npsProblem(text)) return; // refused: the draft keeps its last valid value and Save is blocked below
    const score = text === "" ? null : Number(text);
    setEdit((cur) => {
      const next = { ...cur };
      if (score === (stored.nps ?? null)) delete next.nps; else next.nps = score;
      return next;
    });
  };

  const npsError = npsText !== null && npsProblem(npsText) ? t("hc.npsInvalid") : "";

  const save = async () => {
    if (npsError) return;
    // A cleared text field goes out as null, which is how the server is told to clear it.
    const body = Object.fromEntries(Object.entries(edit).map(([k, v]) => [k, v === "" ? null : v]));
    if (tagEdit.add.length) body.tags_add = tagEdit.add;
    if (tagEdit.remove.length) body.tags_remove = tagEdit.remove;
    let saved;
    try {
      saved = (await api.put(`/super-admin/hq/customers/${customerId}`, body)).data;
    } catch (e) { toast.error(hqCustomerErrorMessage(e, t, "hc.saveFailed")); return; }
    toast.success(t("hc.updated"));
    // The answer is the row as stored after the write, other writers' tags included.
    if (saved?.customer_id === customerId) {
      setData((cur) => ({ ...cur, customer: saved }));
      setEdit({}); setTagEdit(NO_TAG_EDIT); setNpsText(null);
    } else {
      load();
    }
    if ("nps" in body) setAutomationsPending(true);
  };
  const del = async () => {
    if (!window.confirm(t("hc.confirmDelete"))) return;
    try {
      await api.delete(`/super-admin/hq/customers/${customerId}`);
    } catch (e) { toast.error(hqCustomerErrorMessage(e, t, "hc.deleteFailed")); return; }
    toast.success(t("hc.deleted")); onClose();
  };

  if (loadError && !data) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-stretch justify-end" onClick={onClose}>
        <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-white border-l border-slate-200 p-6" data-testid="customer-detail">
          <PageLoadError error={loadError} onRetry={load} testId="customer-detail-load-error" />
        </div>
      </div>
    );
  }
  if (!data) return null;
  const c = data.customer;
  const cur = { ...c, ...edit };
  const readOnly = !canWrite;
  const textField = (key) => (edit[key] !== undefined ? edit[key] : storedText(c, key));
  const dirty = Object.keys(edit).length > 0 || tagEdit.add.length > 0 || tagEdit.remove.length > 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-stretch justify-end" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-white border-l border-slate-200 overflow-y-auto" data-testid="customer-detail">
        <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-slate-100 p-6 flex items-center justify-between z-10">
          <div>
            <div className="font-sans text-2xl">{c.name}</div>
            <div className="text-xs text-slate-500">{c.email} {c.tenant_slug && <>· <code className="text-amber-600">/{c.tenant_slug}</code></>}</div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={refresh} disabled={refreshing} variant="outline" size="sm" className="border-slate-200 text-slate-600"
              aria-label={t("hc.refresh")} title={t("hc.refresh")} data-testid="customer-refresh">
              {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            </Button>
            <button onClick={onClose} aria-label={t("hc.close")}><X className="w-5 h-5 text-slate-400 hover:text-slate-800" /></button>
          </div>
        </div>
        {automationsPending && (
          <div role="status" className="mx-6 mt-4 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800" data-testid="nps-background-note">
            {t("hc.automationsBackground")}
          </div>
        )}

        <div className="p-6 space-y-6">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">{t("hc.stageInfos")}</div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label htmlFor="hc-edit-stage" className={FIELD_LABEL}>{t("hc.colStage")}</label>
                <select id="hc-edit-stage" value={cur.lifecycle_stage} disabled={readOnly} onChange={(e) => setField("lifecycle_stage", e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="edit-stage">
                  {Object.entries(STAGES).map(([k, s]) => <option key={k} value={k} className="bg-white">{t(s.labelKey)}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="hc-edit-phone" className={FIELD_LABEL}>{t("hc.phPhone")}</label>
                <Input id="hc-edit-phone" value={textField("phone")} readOnly={readOnly}
                  onChange={(e) => setField("phone", clampToCodePoints(e.target.value, PHONE_MAX_LENGTH))}
                  className="bg-white border-slate-200" data-testid="edit-phone" />
              </div>
              <div>
                <label htmlFor="hc-edit-company" className={FIELD_LABEL}>{t("hc.phCompany")}</label>
                <Input id="hc-edit-company" value={textField("company")} readOnly={readOnly}
                  onChange={(e) => setField("company", clampToCodePoints(e.target.value, NAME_MAX_LENGTH))}
                  className="bg-white border-slate-200" data-testid="edit-company" />
              </div>
              <div>
                <label htmlFor="hc-edit-nps" className={FIELD_LABEL}>{t("hc.phNps")}</label>
                {/* Text, not type=number: a number input reports any non-numeric text as "" and
                    would silently clear the score. The text is parsed here (npsProblem). */}
                <Input id="hc-edit-nps" type="text" inputMode="numeric" readOnly={readOnly}
                  value={npsText !== null ? npsText : (cur.nps ?? "")} onChange={(e) => changeNps(e.target.value)}
                  className="bg-white border-slate-200" aria-invalid={!!npsError} data-testid="edit-nps" />
                {npsError && <div className="text-xs text-rose-600 mt-1" role="alert" data-testid="nps-error">{npsError}</div>}
              </div>
            </div>
            <label htmlFor="hc-edit-notes" className={`${FIELD_LABEL} mt-3`}>{t("hc.phNotes")}</label>
            <textarea id="hc-edit-notes" rows={3} value={textField("notes")} readOnly={readOnly}
              onChange={(e) => setField("notes", clampToCodePoints(e.target.value, NOTES_MAX_LENGTH))}
              className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="edit-notes" />
            <div className="mt-3">
              <label htmlFor="hc-edit-tags" className={FIELD_LABEL}>{t("hc.tags")}</label>
              <TagEditor tags={shownTags()} onChange={readOnly ? undefined : setTags} t={t} inputId="hc-edit-tags" />
            </div>
            {canWrite && (dirty || npsError) && (
              <Button onClick={save} disabled={!!npsError} className="mt-4 bg-sol-gold text-black hover:bg-amber-200" data-testid="save-customer">{t("hc.save")}</Button>
            )}
          </div>

          {(c.tenant_id || DETACH_STATUSES.includes(c.tenant_status)) && (
            <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid="practice-block">
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">{t("hc.practice")}</div>
              <dl className="grid grid-cols-[8rem_1fr] gap-y-1 text-sm">
                <dt className="text-slate-400">{t("hc.colName")}</dt>
                <dd>{c.tenant_name || "—"}</dd>
                <dt className="text-slate-400">{t("hc.practiceStatus")}</dt>
                <dd>{tenantStatusLabel(t, c.tenant_status)}</dd>
                <dt className="text-slate-400">{t("hc.colPlan")}</dt>
                <dd>{c.tenant_plan ? planName(c.tenant_plan) : "—"}</dd>
                <dt className="text-slate-400">{t("hc.practiceSite")}</dt>
                <dd>
                  {c.website ? (
                    <a href={isWebUrl(c.website) ? c.website : undefined} target="_blank" rel="noopener noreferrer"
                      className="text-amber-600 hover:underline break-all" data-testid="practice-website">{c.website}</a>
                  ) : "—"}
                </dd>
              </dl>
            </div>
          )}

          {/* `owed_to_solunary` is null for a reader without finance.read: no panel at all, since
              "Nothing owed" would be a claim this reader cannot see the basis of. */}
          {data.owed_to_solunary != null && (
            <div className="rounded-xl bg-emerald-400/5 border border-emerald-400/20 p-4" data-testid="hc-ar">
              <div className="text-xs uppercase tracking-wider text-emerald-600 mb-1">{t("hc.arBalance")}</div>
              <div className="text-xs text-slate-500 mb-2" data-testid="hc-ar-explainer">{t("hc.arExplainer")}</div>
              {/* One line per invoice currency: balances in different currencies are never summed. */}
              {currenciesOf([data.owed_to_solunary]).length === 0 ? (
                <div className="font-sans text-sm text-slate-500" data-testid="hc-ar-none">{t("hc.arNone")}</div>
              ) : currenciesOf([data.owed_to_solunary]).map((cur) => (
                <div key={cur} className="font-sans font-semibold text-3xl text-emerald-600" data-testid={`hc-ar-${cur}`}>
                  {formatMoneyLedger(data.owed_to_solunary[cur], cur, lang)}
                </div>
              ))}
            </div>
          )}

          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">{t("hc.communications")}</div>
            <div className="space-y-3 mb-4">
              {data.communications.map((co) => (
                <div key={co.comm_id} className="rounded-lg bg-white border border-slate-100 p-3 text-sm">
                  <div className="flex items-center gap-2 text-xs mb-1 flex-wrap">
                    {co.channel === "email" ? <Mail className="w-3.5 h-3.5 text-amber-600" /> :
                     co.channel === "call" ? <Phone className="w-3.5 h-3.5 text-sol-sage" /> :
                     <MessageSquare className="w-3.5 h-3.5 text-amber-600" />}
                    <span className="text-slate-500">{co.subject || t(`hc.channel.${co.channel}`, undefined, co.channel)}</span>
                    <span className="text-slate-400">· {co.actor_email} · {formatDate(co.at, lang)}</span>
                    <DeliveryBadge t={t} comm={co} />
                  </div>
                  <div className="text-slate-800/70 text-xs whitespace-pre-wrap">{co.body}</div>
                </div>
              ))}
              {data.communications.length === 0 && <div className="text-slate-400 text-sm">{t("hc.noComms")}</div>}
            </div>
            {canWrite && (
              <CommunicationComposer t={t} customerId={customerId} recipientEmail={data.recipient_email ?? null} onSent={refresh} />
            )}
          </div>

          {data.tenant && (
            <a href={`/super-admin/tenants/${data.tenant.tenant_id}`} className="block rounded-xl bg-sol-gold/5 border border-sol-gold/20 p-4 hover:bg-sol-gold/10 transition" data-testid="link-tenant-360">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-amber-600 mb-1">{t("hc.linkedTenant")}</div>
                  <div className="font-medium">{tenantDisplayName(data.tenant)}</div>
                </div>
                <ExternalLink className="w-4 h-4 text-amber-600" />
              </div>
            </a>
          )}

          {canWrite && (
            <Button onClick={del} variant="ghost" className="text-rose-400 hover:bg-rose-50" data-testid="delete-customer">{t("hc.deleteClient")}</Button>
          )}
        </div>
      </div>
    </div>
  );
}

// How an email in the history reached its recipient. `delivery` is null for everything that was only
// logged (calls, meetings, notes, a received email, and every entry from before emails were sent).
const DELIVERY_COLORS = {
  sending: "bg-amber-100 text-amber-700",
  queued: "bg-amber-100 text-amber-700",
  sent: "bg-emerald-100 text-emerald-700",
  failed: "bg-rose-100 text-rose-700",
  unconfirmed: "bg-orange-100 text-orange-700",
};
const PILL = "inline-flex rounded-full px-2 py-0.5 text-xs";

function DeliveryBadge({ t, comm }) {
  const { delivery } = comm;
  if (!delivery) {
    return (
      <span className="inline-flex gap-1" data-testid={`comm-delivery-${comm.comm_id}`} data-status="logged">
        <span className={`${PILL} bg-slate-100 text-slate-600`}>{t("hc.comm.logged")}</span>
        {comm.direction === "inbound" && <span className={`${PILL} bg-slate-100 text-slate-600`}>{t("hc.comm.received")}</span>}
      </span>
    );
  }
  return (
    <span className={`${PILL} ${DELIVERY_COLORS[delivery.status] || "bg-slate-100 text-slate-600"}`}
      data-testid={`comm-delivery-${comm.comm_id}`} data-status={delivery.status}>
      {t(`hc.comm.delivery.${delivery.status}`, { to: delivery.to })}
    </span>
  );
}

// What the server answered, as the sentence the operator sees (contract §5.6). A replay is the same
// message answered again, so it reads exactly like its first answer.
function announceCommunication(t, communication) {
  const delivery = communication?.delivery;
  if (!delivery) { toast.success(t("hc.comm.toastLogged")); return; }
  if (delivery.status === "sent") toast.success(t("hc.comm.toastSent", { to: delivery.to }));
  else if (delivery.status === "queued") toast.info(t("hc.comm.toastQueued"));
  else if (delivery.status === "failed") toast.error(t("hc.comm.toastFailed"));
  else toast.info(t(`hc.comm.delivery.${delivery.status}`, { to: delivery.to }));
}

const newRequestId = () => crypto.randomUUID();

// The composer sends an email to the practice, or logs an exchange that happened elsewhere. Both are
// one POST; `direction` is what tells them apart (email + outbound = sent). Every draft carries an
// idempotency key (`requestId`): an unchanged draft retried after a network error reuses it, so the
// server answers the first attempt again instead of sending twice, and any edit is a new message.
function CommunicationComposer({ t, customerId, recipientEmail, onSent }) {
  const [draft, setDraft] = useState(() => ({
    channel: "email", logReceived: !recipientEmail, subject: "", body: "", requestId: newRequestId(),
  }));
  const [sending, setSending] = useState(false);
  const inFlight = useRef(false); // set synchronously: state alone would let a second click through before the re-render
  const edit = (patch) => setDraft((cur) => ({ ...cur, ...patch, requestId: newRequestId() }));

  const isEmail = draft.channel === "email";
  // Without an address the only email exchange that can be recorded is one that arrived.
  const direction = isEmail && (draft.logReceived || !recipientEmail) ? "inbound" : "outbound";
  const sendsEmail = isEmail && direction === "outbound";
  const subjectMissing = sendsEmail && !hasVisibleCharacter(draft.subject);
  const canSubmit = !sending && draft.body.trim() !== "" && !subjectMissing;

  const submit = async () => {
    if (inFlight.current || !canSubmit) return;
    inFlight.current = true;
    setSending(true);
    try {
      const { data: answer } = await api.post(`/super-admin/hq/customers/${customerId}/communications`, {
        client_request_id: draft.requestId, channel: draft.channel, direction, subject: draft.subject, body: draft.body,
      });
      announceCommunication(t, answer?.communication);
      setDraft((cur) => ({ ...cur, subject: "", body: "", requestId: newRequestId() }));
      onSent();
    } catch (e) {
      toast.error(hqCustomerErrorMessage(e, t, "hc.commFailed"));
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  };

  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid="comm-composer">
      <div className="grid grid-cols-2 gap-2 mb-2">
        <select value={draft.channel} onChange={(e) => edit({ channel: e.target.value })} aria-label={t("hc.communications")}
          className="bg-white border border-slate-200 rounded-md px-2 py-1.5 text-xs" data-testid="comm-channel">
          {CHANNELS.map((x) => <option key={x} value={x} className="bg-white">{t(`hc.channel.${x}`)}</option>)}
        </select>
        <Input placeholder={t("hc.phSubject")} aria-label={t("hc.phSubject")} value={draft.subject}
          onChange={(e) => edit({ subject: clampToCodePoints(e.target.value, COMM_SUBJECT_MAX_LENGTH) })}
          aria-invalid={subjectMissing && draft.subject !== ""} className="bg-white border-slate-200 text-xs" data-testid="comm-subject" />
      </div>
      {isEmail && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2 text-xs text-slate-700" role="radiogroup">
          <label className="inline-flex items-center gap-1.5">
            <input type="radio" name="comm-mode" checked={direction === "outbound"} disabled={!recipientEmail}
              onChange={() => edit({ logReceived: false })} data-testid="comm-mode-send" />
            {t("hc.comm.modeSend")}
          </label>
          <label className="inline-flex items-center gap-1.5">
            <input type="radio" name="comm-mode" checked={direction === "inbound"}
              onChange={() => edit({ logReceived: true })} data-testid="comm-mode-log" />
            {t("hc.comm.modeLogReceived")}
          </label>
        </div>
      )}
      {isEmail && !recipientEmail && (
        <div className="text-xs text-amber-700 mb-2" data-testid="comm-no-recipient">{t("hc.comm.noRecipient")}</div>
      )}
      {sendsEmail ? (
        <div className="text-xs text-slate-500 mb-2" data-testid="comm-send-hint">{t("hc.comm.sendHint", { to: recipientEmail })}</div>
      ) : (
        <div className="text-xs text-slate-500 mb-2" data-testid="comm-log-hint">{t("hc.comm.logHint")}</div>
      )}
      <textarea rows={2} placeholder={t("hc.phContent")} aria-label={t("hc.phContent")} value={draft.body}
        onChange={(e) => edit({ body: clampToCodePoints(e.target.value, COMM_BODY_MAX_LENGTH) })}
        className="w-full bg-white border border-slate-200 rounded-md px-2 py-1.5 text-xs" data-testid="comm-body" />
      {subjectMissing && <div className="text-xs text-rose-600 mt-1" role="alert" data-testid="comm-subject-required">{t("hc.comm.subjectRequired")}</div>}
      <Button onClick={submit} disabled={!canSubmit} size="sm" className="mt-2 bg-sol-gold text-black hover:bg-amber-200" data-testid="add-comm">
        {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : sendsEmail && <Send className="w-4 h-4 mr-2" />}
        {sendsEmail ? t("hc.comm.send") : t("hc.comm.log")}
      </Button>
    </div>
  );
}

function NewCustomerModal({ t, onClose, onDone, onOpenExisting }) {
  const [form, setForm] = useState({ email: "", name: "", company: "", phone: "", lifecycle_stage: "lead", notes: "" });
  // A duplicate email is shown inside the form with a way to the existing client; it is not a toast.
  const [duplicateOf, setDuplicateOf] = useState("");
  const [nameError, setNameError] = useState("");
  const set = (key, value) => setForm({ ...form, [key]: value });
  const submit = async (e) => {
    e.preventDefault();
    setDuplicateOf("");
    const problem = nameProblem(form.name);
    if (problem) { setNameError(t(NAME_PROBLEM_KEYS[problem], { max: NAME_MAX_LENGTH })); return; }
    try {
      await api.post("/super-admin/hq/customers", form);
      toast.success(t("hc.created")); onDone();
    } catch (err) {
      const existing = duplicateEmailCustomerId(err);
      if (existing) setDuplicateOf(existing);
      else toast.error(hqCustomerErrorMessage(err, t, "hc.saveFailed"));
    }
  };
  // Lengths are the server's, counted in code points (an emoji is one character on both sides).
  const field = (key, max) => ({ value: form[key], onChange: (e) => set(key, clampToCodePoints(e.target.value, max)) });
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="new-customer-modal"
        className="bg-white border border-slate-200 rounded-2xl p-6 max-w-md w-full space-y-3">
        <h3 className="font-sans text-xl mb-1">{t("hc.newClient")}</h3>
        <div>
          <label htmlFor="hc-new-email" className={FIELD_LABEL}>{t("hc.colEmail")}</label>
          <Input id="hc-new-email" required type="email" value={form.email} onChange={(e) => { set("email", e.target.value); setDuplicateOf(""); }}
            className="bg-white border-slate-200" data-testid="new-email" />
        </div>
        {duplicateOf && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" role="alert" data-testid="new-customer-error">
            {t("hc.emailExists")}{" "}
            <button type="button" onClick={() => onOpenExisting(duplicateOf)} className="underline font-medium" data-testid="open-existing-customer">
              {t("hc.openExisting")}
            </button>
          </div>
        )}
        <div>
          <label htmlFor="hc-new-name" className={FIELD_LABEL}>{t("hc.phName")}</label>
          <Input id="hc-new-name" required {...field("name", NAME_MAX_LENGTH)}
            onChange={(e) => { set("name", clampToCodePoints(e.target.value, NAME_MAX_LENGTH)); setNameError(""); }}
            aria-invalid={!!nameError} className="bg-white border-slate-200" data-testid="new-name" />
          {nameError && <div className="text-xs text-rose-600 mt-1" role="alert" data-testid="new-name-error">{nameError}</div>}
        </div>
        <div>
          <label htmlFor="hc-new-company" className={FIELD_LABEL}>{t("hc.phCompany")}</label>
          <Input id="hc-new-company" {...field("company", NAME_MAX_LENGTH)} className="bg-white border-slate-200" data-testid="new-company" />
        </div>
        <div>
          <label htmlFor="hc-new-phone" className={FIELD_LABEL}>{t("hc.phPhone")}</label>
          <Input id="hc-new-phone" {...field("phone", PHONE_MAX_LENGTH)} className="bg-white border-slate-200" data-testid="new-phone" />
        </div>
        <div>
          <label htmlFor="hc-new-notes" className={FIELD_LABEL}>{t("hc.phNotes")}</label>
          <textarea id="hc-new-notes" rows={3} {...field("notes", NOTES_MAX_LENGTH)}
            className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="new-notes" />
        </div>
        <div>
          <label htmlFor="hc-new-stage" className={FIELD_LABEL}>{t("hc.colStage")}</label>
          <select id="hc-new-stage" value={form.lifecycle_stage} onChange={(e) => set("lifecycle_stage", e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="new-stage">
            {Object.entries(STAGES).map(([k, s]) => <option key={k} value={k} className="bg-white">{t(s.labelKey)}</option>)}
          </select>
        </div>
        <Button type="submit" className="w-full bg-sol-gold text-black hover:bg-amber-200" data-testid="submit-new-customer">{t("hc.create")}</Button>
      </form>
    </div>
  );
}
